import React, { useLayoutEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { Plus, Trash2 } from "lucide-react";
import {
  createEmptyQuestion,
  type SubCapabilityQuestionDraft,
  type SubCapabilityQuestionStateKind,
} from "../utils/subCapabilityQuestionUtils";

/** Textarea that starts compact and grows to fit its text; still manually resizable. */
const AutoGrowTextarea = ({
  value,
  ...props
}: { value: string } & React.ComponentProps<typeof Form.Control>) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    // Width changes (maximize / window resize) re-wrap the text.
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [value]);

  return <Form.Control as="textarea" ref={ref} value={value} {...props} />;
};

type SubCapabilityQuestionListEditorProps = {
  label: string;
  questions: SubCapabilityQuestionDraft[];
  stateKind: SubCapabilityQuestionStateKind;
  onChange: (next: SubCapabilityQuestionDraft[]) => void;
  disabled?: boolean;
  placeholder?: string;
  addLabel?: string;
  className?: string;
  numbered?: boolean;
  /** Start one row tall and expand to fit longer questions. */
  autoGrow?: boolean;
};

const SubCapabilityQuestionListEditor = ({
  label,
  questions,
  stateKind,
  onChange,
  disabled = false,
  placeholder = "Enter a question",
  addLabel = "Add question",
  className,
  numbered = false,
  autoGrow = false,
}: SubCapabilityQuestionListEditorProps) => {
  const rows = questions?.length ? questions : [createEmptyQuestion(stateKind, 0)];

  const updateAt = (index: number, questionText: string) => {
    const next = rows.map((question, i) =>
      i === index ? { ...question, questionText } : question
    );
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, createEmptyQuestion(stateKind, rows.length)]);
  };

  const removeAt = (index: number) => {
    if (rows.length <= 1) {
      onChange([createEmptyQuestion(stateKind, 0)]);
      return;
    }
    onChange(rows.filter((_, i) => i !== index).map((question, i) => ({
      ...question,
      displayOrder: i,
    })));
  };

  return (
    <div className={className ? `${className} cap-mgmt-question-list` : "cap-mgmt-question-list"}>
      {!numbered ? (
        <div className="cap-mgmt-question-list__header">
          <Form.Label className="cap-mgmt-field-label mb-0">{label}</Form.Label>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="cap-mgmt-inline-btn"
            onClick={addRow}
            disabled={disabled}
          >
            <Plus className="me-1" />
            {addLabel}
          </Button>
        </div>
      ) : null}
      {rows.map((question, index) => (
        <div
          key={`${question.id || "new"}-${stateKind}-${index}`}
          className={numbered ? "cap-form-question-row" : "cap-mgmt-question-row"}
        >
          {numbered ? (
            <span className="cap-form-question-row__index" aria-hidden>{index + 1}</span>
          ) : null}
          {autoGrow ? (
            <AutoGrowTextarea
              rows={1}
              size="sm"
              placeholder={placeholder}
              value={question.questionText || ""}
              disabled={disabled}
              onChange={(e) => updateAt(index, e.target.value)}
            />
          ) : (
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              placeholder={placeholder}
              value={question.questionText || ""}
              disabled={disabled}
              onChange={(e) => updateAt(index, e.target.value)}
            />
          )}
          <Button
            type="button"
            variant="outline-danger"
            size="sm"
            className="cap-mgmt-icon-btn"
            aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
            disabled={disabled || (rows.length === 1 && !String(question.questionText || "").trim())}
            onClick={() => removeAt(index)}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      {numbered ? (
        <button
          type="button"
          className="cap-form-add-question"
          onClick={addRow}
          disabled={disabled}
        >
          <Plus size={14} aria-hidden />
          {addLabel}
        </button>
      ) : null}
    </div>
  );
};

export default SubCapabilityQuestionListEditor;
